/**
 * SOLID Review
 * S — Strong. One concern: the table's seats in join order — built fresh from the server's roster (the lobby),
 *     merged into the seats held (a game in progress) — and who decides after the roller.
 * O — Good. A new default seat field is added once, in `seat`, and both builders pick it up; a new rotation rule
 *     is one more query.
 * L — N/A — no inheritance hierarchy (private constructor).
 * I — Strong. Two factories, the merge, two queries, the snapshot.
 * D — Strong. The Domain's enums, the seat schema type and the table's lives.
 */
import { PlayerStatus, MonetizationTrack, INITIAL_LIVES } from '../uml-components/primitives';
import type { TableLives } from './TableLives';
import type { IEntityClientSeat } from '../ports/schemas/ClientTable';

/** A roster entry as the server sends it: `roomUpdated`, a `room` read, a join's answer. */
export interface IEntityRosterUpdatePlayer {
  readonly id: string;
  readonly displayName: string;
}

/** The server's list, which an older or failing server may send as nothing at all. */
type TRosterList = ReadonlyArray<IEntityRosterUpdatePlayer> | null | undefined;

/**
 * THE SEATS, AS A VALUE OBJECT (frontend-domain-oo P3, 2026-10-26; handbook §28.270) — `game/Roster.ts`'s two
 * builders and `game/ChallengerSelection.ts`'s rotation, on the seats they read.
 *
 * The builders were moved to the Domain in FE1 (2026-10-03): `buildRosterFromRaw` from
 * application/mappers/buildRosterFromRaw.ts, shared by the two lobby roster hooks, and `mergeRoster` from
 * ControlPanel's `mergeRoomRoster`, which took and returned a whole room. The default seat they both spelled out
 * is written once, in `seat`.
 */
export class Roster {
  private constructor(private readonly seats: ReadonlyArray<IEntityClientSeat>) {}

  /** The seats a room holds. */
  static of(seats: ReadonlyArray<IEntityClientSeat>): Roster {
    return new Roster(seats);
  }

  /** The lobby's roster: every seat fresh, from the server's list; none for no list. */
  static fresh(players: TRosterList, localPlayerId: string | null | undefined): Roster {
    return new Roster((players ?? []).map((p, i) => Roster.seat(p, i, localPlayerId)));
  }

  /** A seat the client has not seen before: full lives, ACTIVE, free tier, at its position in the list. */
  private static seat(p: IEntityRosterUpdatePlayer, seatIndex: number, localPlayerId: string | null | undefined): IEntityClientSeat {
    return {
      id: p.id,
      displayName: p.displayName,
      avatarUrl: null,
      status: PlayerStatus.ACTIVE,
      seatIndex,
      isLocalPlayer: p.id === localPlayerId,
      isCurrentTurn: false,
      lives: { current: INITIAL_LIVES, max: INITIAL_LIVES, isEliminated: false },
      monetization: { track: MonetizationTrack.FREE, matchCredits: 0, hasPremiumAccess: false },
    };
  }

  /**
   * A ROOM_UPDATED roster merged into these seats.
   *
   * The rule that matters: a player already seated is carried over untouched. The update is name-and-id only,
   * so rebuilding every seat from it would reset lives, seat index, suspension status and monetization for
   * everyone in the room each time anybody joined — a late join as a silent game-state reset for the players
   * already there.
   *
   * New players are seeded at full lives and ACTIVE, which is what the server has just done for them, at their
   * position in the incoming list. A player the update no longer lists is dropped; no list drops everyone
   * rather than throwing — a server anomaly must leave the client wrong, not out of sync for good.
   */
  merged(players: TRosterList, localPlayerId: string | null | undefined): Roster {
    const held = new Map(this.seats.map((s) => [s.id, s]));
    return new Roster((players ?? []).map((p, i) => held.get(p.id) ?? Roster.seat(p, i, localPlayerId)));
  }

  /** Every seat's player, in join order. */
  ids(): string[] {
    return this.seats.map((s) => s.id);
  }

  /**
   * WHO DECIDES WHEN THE SERVER HAS NAMED NOBODY: the next live, non-suspended seat after the roller, in circular
   * join order (`getChallengerId`, once an exact duplicate inline in both ControlPanel.tsx and AppRoot.tsx).
   *
   * If every live candidate is suspended (two players, the opponent backgrounded), the next live seat regardless:
   * the decider must always be a real player. The server's suspend → countdown → auto-believe machinery owns a
   * suspended decider, but only when it has an id to key on; answering nobody here made the announce send
   * challengerId '' and poisoned the round (SUS-DEC). Nobody without a roller, a seat for them, or a second seat.
   */
  challengerAfter(rollerId: string | null, lives: TableLives, suspendedIds: ReadonlyArray<string>): string | null {
    if (!rollerId || this.seats.length < 2) return null;
    const rollerIndex = this.seats.findIndex((s) => s.id === rollerId);
    if (rollerIndex === -1) return null;
    const rotation = [...this.seats.slice(rollerIndex + 1), ...this.seats.slice(0, rollerIndex)];
    const decider = rotation.find((s) => lives.isAlive(s.id) && !suspendedIds.includes(s.id))
      ?? rotation.find((s) => lives.isAlive(s.id));
    return decider === undefined ? null : decider.id;
  }

  /** The seats as a room holds them — a new list; a seat carried over is the same seat. */
  toSnapshot(): IEntityClientSeat[] {
    return [...this.seats];
  }
}
